import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SolEstimatePricingMode, SolMaterialEscalationType } from '../../../models/enum';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SolEstimateTableMaterialCostsRow } from '../../../models/sol-estimate-table/SolEstimateTableMaterialCostsRow';
import { SolEstimateTableCommonFunctionsService } from '../../../services/sol-estimate-table-common-functions/sol-estimate-table-common-functions.service';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MultilingualCurrencyPipe } from "../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { DecimalPrecisionPipe } from "../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { NumericMaskDirective } from '../../../shared/numeric/numeric-mask.directive';
import { SolEstimateTableRowBase } from '../../../models/sol-estimate-table/SolEstimateTableRowBase';
import { EscalationData, Escalations, EstimateTab } from '../../../models/dtos';
import { SolMaterialEscalationDialogDataModel } from '../../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { CommonDataService } from '../../../services/common-data.service';
import { CONSTANTS } from '../../../Constants/Constants';
import { MaterialSubRowKey, MaterialSellPriceFlag, MATERIAL_SELL_PRICE_FLAGS, MATERIAL_CATEGORY_BY_LABEL } from '../../../services/estimate/estimate-last-modified-sell-price.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sol-estimate-table-material-type',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    CommonModule,
    TranslatePipe,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    NumericMaskDirective,
    MultilingualCurrencyPipe,
    DecimalPrecisionPipe
],
  templateUrl: './sol-estimate-table-material-type.component.html',
  styleUrl: './sol-estimate-table-material-type.component.scss'
})
export class SolEstimateTableMaterialTypeComponent implements OnInit, OnDestroy {

  @Input() isMaterialExpanded: boolean = false;
  @Input() isMarkUpSelected: boolean = false;
  @Input() sellAsPricingMode: SolEstimatePricingMode = SolEstimatePricingMode.Line_Item;
  @Input() materialRowForm!: FormGroup<SolEstimateTableMaterialCostsRow>;
  @Input() sectionLabel: string = 'MATERIAL_COSTS';
  @Input() isCollaborativeNotEdit: boolean = false;

  destroy$: Subject<void> = new Subject<void>();

  solEstimatePricingMode = SolEstimatePricingMode;

  /** Live EstimateTab, read directly by the template so flag updates are picked up in place. */
  estimateTab: EstimateTab | null = null;

  /** EstimateTab flag name per row of this material type. */
  sellPriceFlags: Record<MaterialSubRowKey, MaterialSellPriceFlag> = MATERIAL_SELL_PRICE_FLAGS.siemensMaterial;

  get isEstimateSapSubmitted(): boolean {
    return this.solEstimateTableCommonFunctionsService.isEstimateSapSubmitted;
  }

  constructor(
    private readonly commonDataService: CommonDataService,
    public readonly solEstimateTableCommonFunctionsService: SolEstimateTableCommonFunctionsService
  ) { }

  ngOnInit(): void {
    if(!this.materialRowForm){
      return;
    }
    this.commonDataService.projectObjectDataAvailable$
    .pipe(takeUntil(this.destroy$))
    .subscribe((isAvailable: boolean) => {
      if (isAvailable) {
        this.estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab ?? null;
        const category = MATERIAL_CATEGORY_BY_LABEL[this.sectionLabel];
        if (category) {
          this.sellPriceFlags = MATERIAL_SELL_PRICE_FLAGS[category];
        }
      }
    });
  }

  get isSubsidyValuePresent(): boolean {
    return this.materialRowForm.controls.isSubsidyValuePresent?.value ?? false;
  }

  get headerRowFormGroup(): FormGroup<SolEstimateTableRowBase> | null{
    return this.materialRowForm.controls.headerRow ?? null;
  }

  get shippingAndHandlingFormGroup(): FormGroup<SolEstimateTableRowBase> | null{
    return this.materialRowForm.controls.shippingAndHandling ?? null;
  }

  get warrantyFormGroup(): FormGroup<SolEstimateTableRowBase> | null{
    return this.materialRowForm.controls.warranty ?? null;
  }

  openShippingAndHandlingDialog(): void {
    if(this.commonDataService.isReadOnlyMode()) {
      return;
    }
    this.solEstimateTableCommonFunctionsService.openShippingAndHandlingDialog();
  }

  openWarrantyDialog(): void {
    if(this.commonDataService.isReadOnlyMode()) {
      return;
    }
    this.solEstimateTableCommonFunctionsService.openWarrantyDialog();
  }

  openEscalationDialog(sectionLabel: string): void {
    if (this.commonDataService.isReadOnlyMode()) {
      return;
    }

    // open estimate API Integration
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const escalationRates = estimateMetaData?.escalationRates ?? new Escalations();
    let escalationDialogData: SolMaterialEscalationDialogDataModel = {
      escalationData: new EscalationData(),
      sectionLabel: sectionLabel,
      escalationType: CONSTANTS.MATERIAL,
      applyMaterialEscalationToAll: escalationRates.applyMaterialEscalationToAll,
      applyLaborEscalationToAll: escalationRates.applyLaborEscalationToAll,
      isEscalationDisabled: this.isEstimateSapSubmitted
    }
    switch(sectionLabel){
      case SolMaterialEscalationType.Siemens_Material:
        escalationDialogData.escalationData = escalationRates.siemensMaterialEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolMaterialEscalationType.Outside_Material:
        escalationDialogData.escalationData = escalationRates.outsideMaterialEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolMaterialEscalationType.Mechanical_Material:
        escalationDialogData.escalationData = escalationRates.mechanicalMaterialEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolMaterialEscalationType.Electrical_Material:
        escalationDialogData.escalationData = escalationRates.electricalMaterialEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      default:
        break;
    }
  }

  get isEstimateClosed(): boolean {
    return this.commonDataService.getEstimateId() === CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
